// Search-param coercion + URL-builder used by the triage board route.
// Lives in a sibling file (not inline) so the route stays focused on
// the layout JSX, and so unit tests can pin the parser shape directly
// without rendering the route. (DEV-16210 cleanup.)
import type { RunTrigger } from '@cortex/types';

import { runStatuses } from '../../lib/constants';

import { type IRunFilterState } from './RunFilters';

// The color → header-class map used to live here. It moved to
// `lib/board-column-colors.ts` (DEV-17060) once the columns admin list and
// the workflow-builder edge chip needed the same palette — see that file for
// why the three surfaces disagreed.
//
// `mode` (outcome | status) and the `__untagged` sentinel also used to live
// here. DEV-17093 removed the view toggle, and DEV-17061 replaced "Untagged"
// with the Pending/Finished spine — the column model now lives in
// `triage-columns.ts`. A stale `?mode=status` in a bookmarked URL is simply
// dropped by `parseTriageSearch` (it whitelists known keys), which lands the
// user on the one board with their filters intact.

export interface ITriageSearch extends IRunFilterState {
  /**
   * Free-text search across workflow name, run id, run.status, and
   * run.error.message. Client-side filter applied to the already-
   * fetched run list — the list query polls every 2–30 s anyway so a
   * server-side search isn't worth the round-trip. (DEV-16180.)
   */
  q?: string;
  /**
   * Run id whose detail dialog should be open. Driven by the URL via
   * the parent layout — bookmarking `/triage/runs/<id>` lands on the
   * board with the dialog already open. (DEV-16210.)
   */
  runId?: string;
}

/** Build a `/v1/runs?…` URL from the current filter state. */
export function buildRunsUrl(filters: IRunFilterState): string {
  const qp = new URLSearchParams();
  qp.set('limit', '200');
  if (filters.status) qp.set('status', filters.status);
  if (filters.workflowId) qp.set('workflowId', filters.workflowId);
  if (filters.trigger) qp.set('trigger', filters.trigger);
  if (filters.since) qp.set('since', filters.since);
  if (filters.until) qp.set('until', filters.until);
  return `v1/runs?${qp.toString()}`;
}

/** Validate the raw search object into a typed `ITriageSearch`. */
export function parseTriageSearch(raw: Record<string, unknown>): ITriageSearch {
  return pickDefined({
    status: pickEnum(raw.status, runStatuses),
    workflowId: pickString(raw.workflowId),
    trigger: pickEnum<RunTrigger>(raw.trigger, ['MANUAL', 'API', 'WEBHOOK', 'SCHEDULED']),
    since: pickString(raw.since),
    until: pickString(raw.until),
    q: pickString(raw.q),
    runId: pickString(raw.runId),
  });
}

function pickDefined(raw: ITriageSearch): ITriageSearch {
  const out: ITriageSearch = {};
  if (raw.status) out.status = raw.status;
  if (raw.workflowId) out.workflowId = raw.workflowId;
  if (raw.trigger) out.trigger = raw.trigger;
  if (raw.since) out.since = raw.since;
  if (raw.until) out.until = raw.until;
  if (raw.q) out.q = raw.q;
  if (raw.runId) out.runId = raw.runId;
  return out;
}

function pickString(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

function pickEnum<T extends string>(v: unknown, allowed: readonly T[]): T | undefined {
  return typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : undefined;
}
